<template>
  <va-form
    :id="isEdit ? favoritesBlock.id : ''"
    class="create-favorites-block__form"
    ref="form"
    :item="favoritesBlock"
    v-model="form"
    :before-save="beforeSave"
    :after-save="afterSave"
    :modify-before-send="modifyBeforeSend"
    :custom-redirect-route="
      !!this.$route.query.domainId
        ? {
            name: 'domains_show',
            params: { id: domainId },
            query: { tab: 'pages_add_ons' },
          }
        : {
            name: 'favorites_blocks_list',
          }
    "
    :required-fields-set="requiredFieldsSet">
    <!-- Details -->
    <v-card class="spacing-bottom--16">
      <div class="d-flex">
        <div class="d-flex flex-column">
          <v-card-title>{{ $t("resources.favorites_blocks.favorites_block_details_block_title") }}</v-card-title>
          <v-card-subtitle class="spacing-bottom--16">{{
            $t("resources.favorites_blocks.favorites_block_details_block_subtitle")
          }}</v-card-subtitle>
        </div>
        <v-card-actions class="ml-auto">
          <v-checkbox
            color="primary"
            v-model="form.isDefault"
            :label="$t('resources.favorites_blocks.set_as_default')"></v-checkbox>
        </v-card-actions>
      </div>
      <div class="spacing-bottom--16">
        <va-text-input
          v-model="form.name"
          required
          source="name"
          :label="$t('resources.favorites_blocks.favorites_block_name')"
          hide-details
          :placeholder="$t('resources.favorites_blocks.favorites_block_name_placeholder')"></va-text-input>
        <span class="favorites-block-name-subtitle">{{
          $t("resources.favorites_blocks.favorites_block_name_subtitle")
        }}</span>
      </div>
      <va-autocomplete-input
        v-model="form.domain"
        source="domain"
        :disabled="isDomainProvided"
        required
        :placeholder="$t('resources.favorites_blocks.favorites_block_domain_placeholder')"
        :label="$t('resources.favorites_blocks.favorites_block_domain')"
        :choices="domainsList"
        :min-chars="1"
        cache-items
        @change="domainChanged"></va-autocomplete-input>

      <div class="page-wrapper" v-if="form.showOnPages && form.showOnPages.length > 0">
        <div class="page-label">
          {{ $t("resources.favorites_blocks.fields.page") }}
        </div>
        <div class="chip-container">
          <v-chip
            color="primary-20"
            text-color="secondary-btn"
            close
            close-icon="mdi-close"
            @click:close="removePage(page)"
            v-for="page in getPageArray"
            :key="page.id">
            {{ getPageName(page) }}
          </v-chip>
          <div v-if="form.showOnPages.length > 12">
            <v-btn
              v-if="!showAllPageChips"
              text
              color="primary"
              class="chip-action-button"
              @click="showAllPageChips = true"
              :ripple="false">
              {{ $t("resources.calendars.show_all") }}
            </v-btn>
            <v-btn
              v-else
              text
              color="primary"
              class="chip-action-button"
              @click="showAllPageChips = false"
              :ripple="false">
              {{ $t("resources.calendars.collapse") }}
            </v-btn>
          </div>
        </div>
      </div>
    </v-card>
    <v-card>
      <v-card-title>{{ $t("resources.favorites_blocks.favorites_blocks_items_block_title") }}</v-card-title>
      <v-card-subtitle class="spacing-bottom--11">
        {{ $t("resources.favorites_blocks.favorites_blocks_items_block_subtitle") }}
      </v-card-subtitle>
      <va-array-input
        v-slot="props"
        v-model="form.favorites"
        source="favorites"
        :label="false"
        :add-button-text="$t('generic.add_menu_link')">
        <va-autocomplete-input
          class="page-autocomplete"
          v-model="form.favorites[props.index].page"
          v-bind="props"
          :placeholder="$t('generic.select_page')"
          source="page"
          :label="$t('resources.favorites_blocks.favorites_blocks_item_label')"
          reference="pages"
          item-value="@id"
          :filter="{ ...resourcesFilter, status: [1, 2] }"
          required
          searchQuery="headline"
          :min-chars="0"
          :items-per-page="5"
          showLoadMore></va-autocomplete-input>
        <v-select
          class="spacing-left--16"
          v-model="form.favorites[props.index].action"
          persistent-placeholder
          :menu-props="{ offsetY: true, offsetOverflow: true }"
          :hide-details="'auto'"
          :label="$t('generic.open_in')"
          :items="MENU_TAB_TYPES">
          <template v-slot:append>
            <v-icon>mdi-chevron-down</v-icon>
          </template>
        </v-select>
        <va-number-input
          style="display: none"
          disabled
          :value="(form.favorites[props.index].position = props.index + 1)"
          required
          v-bind="props"
          source="position"></va-number-input>
      </va-array-input>
    </v-card>
    <required-field-text></required-field-text>
  </va-form>
</template>

<script>
  // Mixins
  import crudFormMixin from "@/mixins/crudFormMixin";
  import ConnectedDomainsList from "@/mixins/connectedDomainsList";
  import providedDomain from "@/mixins/providedDomain";
  //Utils
  import { formatMenuItems } from "@/utility/formatMenuItems";
  // Components
  import RequiredFieldText from "@/components/custom/RequiredFieldText";
  // Constants
  import { MENU_TAB_TYPES } from "../../constant/index";

  export default {
    name: "FavoritesBlockForm",
    components: {
      RequiredFieldText,
    },
    mixins: [ConnectedDomainsList, crudFormMixin, providedDomain],
    props: {
      favoritesBlock: {
        type: Object,
        default: () => {},
      },
      // Determines if form is used while editing
      isEdit: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        MENU_TAB_TYPES,
        requiredFormFields: ["name", "domain"],
        form: {},
        oldDomain: "",
        oldPages: [],
        showAllPageChips: false,
      };
    },
    computed: {
      domainId() {
        return this.form.domain?.replace("/api/domains/", "");
      },
      resourcesFilter() {
        return this.form.domain ? { domain: this.form.domain.replace("/api/domains/", "") } : null;
      },
      getPageArray() {
        if (this.showAllPageChips) {
          return this.form.showOnPages;
        } else {
          return this.form.showOnPages.slice(0, 12);
        }
      },
    },
    beforeMount() {
      this.form = this.favoritesBlock ? this.favoritesBlock : {};
      if (!this.isEdit) {
        delete this.form["@context"];
        delete this.form["@id"];
        delete this.form["@type"];
      }
      this.form.domain = this.favoritesBlock?.domain["@id"];
      this.form.isDefault = this.favoritesBlock?.isDefault || false;
      this.form.favorites = formatMenuItems(this.form.favorites);
      this.form.showOnPages = this.favoritesBlock?.showOnPages || [];
      this.oldDomain = this.favoritesBlock?.domain;
      this.oldPages = this.favoritesBlock?.showOnPages || [];
    },
    methods: {
      getPageName(page) {
        return page.headline.length > 30 ? page.headline.substring(0, 30) + "..." : page.headline;
      },
      removePage(page) {
        this.form.showOnPages = this.form.showOnPages.filter(item => item["@id"] !== page["@id"]);
        this.oldPages = this.form.showOnPages;
      },
      modifyBeforeSend(data) {
        data.showOnPages = data.showOnPages.map(page => page["@id"]);

        return data;
      },
      domainChanged(domain) {
        if (this.form.favorites) {
          this.form.favorites = this.form.favorites.map(item => {
            item.page = null;

            return item;
          });
        }
        if (this.oldDomain !== domain) {
          this.form.showOnPages = [];
        } else {
          this.form.showOnPages = this.oldPages;
        }
      },
      submit() {
        this.$refs.form.onSubmit();
      },
    },
    watch: {
      "form.favorites": {
        handler: function (newVal) {
          newVal.map(item => {
            if (!item.action) {
              item.action = MENU_TAB_TYPES[0].value;
            }
          });
        },
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "../../@core/preset/preset/functions.scss";
  @import "../../@core/preset/preset/typography/index.scss";

  .page-autocomplete {
    flex: 3 !important;
  }
  .page-label {
    @include text-caption-bold();
    color: get-color("subtitle");
    margin-bottom: 12px;
  }

  .page-wrapper {
    margin-top: 16px;
    margin-bottom: -4px;
  }

  .chip-container {
    display: flex;
    flex-wrap: wrap;
    column-gap: 8px;
    row-gap: 6px;
    align-items: center;

    .chip-action-button.v-btn {
      padding: 5px 10px !important;
      height: unset !important;
      min-width: unset;
      color: get-color("secondary-btn") !important;
    }
  }

  .favorites-block-name-subtitle {
    @include text-caption();
    color: get-color("subtitle");
  }
</style>
